import { useState, useEffect } from 'react';
import { getAvatarColor } from '../utils/dashboardState';
import { axiosInstance } from '../services/api';

/**
 * Dashboard Data Hook
 *
 * Fetches dashboard data from API (or returns mock data for testing).
 * Accepts an optional activeFamilyId parameter to re-fetch when
 * the active family changes. The X-Family-Id header is set by
 * the axios interceptor from localStorage.
 */
export function useDashboardData(useMockData = false, activeFamilyId = null) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (useMockData) {
          const mockData = getMockDashboardData('active');
          await new Promise(resolve => setTimeout(resolve, 500));
          setData(mockData);
        } else {
          // X-Family-Id header is injected by axios interceptor
          const response = await axiosInstance.get('/dashboard/');
          setData(response.data);
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [useMockData, activeFamilyId]);

  const refetch = () => {
    setLoading(true);
    setError(null);
  };

  return { data, loading, error, refetch };
}

/**
 * Generate mock dashboard data for testing
 */
function getMockDashboardData(state) {
  const baseUser = {
    firstName: 'Margaret',
    avatarInitial: 'M'
  };

  const mockConnections = [
    {
      id: '1',
      firstName: 'David',
      lastName: 'Thompson',
      relationship: 'Your Son',
      isMinor: false,
      avatarColor: getAvatarColor(0),
      isOnline: true,
      hasActivity: true,
      activityText: 'Asked question'
    },
    {
      id: '2',
      firstName: 'Lisa',
      lastName: 'Thompson',
      relationship: 'Your Daughter',
      isMinor: false,
      avatarColor: getAvatarColor(1),
      isOnline: false,
      hasActivity: false
    },
    {
      id: '3',
      firstName: 'Sarah',
      lastName: 'Anderson',
      relationship: 'Granddaughter',
      isMinor: false,
      avatarColor: getAvatarColor(2),
      isOnline: true,
      hasActivity: true,
      activityText: 'Commented on story'
    },
    {
      id: '4',
      firstName: 'Emma',
      relationship: 'Granddaughter',
      isMinor: true,
      guardianId: '1',
      guardianName: 'David',
      avatarColor: getAvatarColor(3),
      hasActivity: false
    },
    {
      id: '5',
      firstName: 'Tommy',
      relationship: 'Grandson',
      isMinor: true,
      guardianId: '1',
      guardianName: 'David',
      avatarColor: getAvatarColor(4),
      hasActivity: false
    },
    {
      id: '6',
      firstName: 'Jack',
      relationship: 'Grandson',
      isMinor: true,
      guardianId: '2',
      guardianName: 'Lisa',
      avatarColor: getAvatarColor(5),
      hasActivity: false
    }
  ];

  const mockQuestions = [
    {
      id: 'q1',
      text: 'Grandma, how did you and Grandpa meet?',
      askedBy: mockConnections[2], // Sarah
      askedAt: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      isFromMinor: false
    },
    {
      id: 'q2',
      text: 'What was your favorite thing to do at my age?',
      askedBy: mockConnections[3], // Emma (minor)
      askedAt: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
      isFromMinor: true,
      viaParent: 'David'
    },
    {
      id: 'q3',
      text: 'Do you have any photos from when you were a little girl?',
      askedBy: mockConnections[0], // David
      askedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      isFromMinor: false
    }
  ];

  const mockStories = [
    {
      id: 's1',
      title: 'Sunday dinners at my childhood home',
      icon: '🍗',
      sharedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
      hasNewActivity: true,
      engagement: {
        commentCount: 3,
        reactionCount: 5,
        newComments: 3,
        newReactions: 2,
        readBy: [mockConnections[0], mockConnections[1], mockConnections[2]]
      },
      audience: mockConnections
    },
    {
      id: 's2',
      title: 'My first day of school',
      icon: '🎒',
      sharedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000), // 10 days ago
      hasNewActivity: false,
      engagement: {
        commentCount: 2,
        reactionCount: 4,
        newComments: 0,
        newReactions: 0,
        readBy: [mockConnections[0], mockConnections[1]]
      },
      audience: mockConnections
    },
    {
      id: 's3',
      title: 'How I met your grandfather',
      icon: '💕',
      sharedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
      hasNewActivity: false,
      engagement: {
        commentCount: 5,
        reactionCount: 8,
        newComments: 0,
        newReactions: 0,
        readBy: mockConnections.slice(0, 4)
      },
      audience: mockConnections
    }
  ];

  const mockActivities = [
    {
      id: 'a1',
      person: mockConnections[2], // Sarah
      action: 'commented',
      targetTitle: 'Sunday dinners',
      timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000) // 1 hour ago
    },
    {
      id: 'a2',
      person: mockConnections[3], // Emma (minor)
      action: 'reacted',
      targetTitle: 'Sunday dinners',
      timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000) // 3 hours ago
    },
    {
      id: 'a3',
      person: mockConnections[0], // David
      action: 'read',
      targetTitle: 'My first day of school',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000) // 6 hours ago
    },
    {
      id: 'a4',
      person: mockConnections[1], // Lisa
      action: 'commented',
      targetTitle: 'How I met your grandfather',
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000) // 12 hours ago
    }
  ];

  // Generate data based on state
  switch (state) {
    case 'fresh':
      return {
        user: baseUser,
        stats: {
          connectionCount: 0,
          storyCount: 0,
          adultCount: 0,
          minorCount: 0
        },
        connections: [],
        questions: [],
        stories: [],
        activities: [],
        notificationCount: 0
      };

    case 'audience':
      return {
        user: baseUser,
        stats: {
          connectionCount: 6,
          storyCount: 0,
          adultCount: 3,
          minorCount: 3
        },
        connections: mockConnections,
        questions: mockQuestions,
        stories: [],
        activities: [],
        notificationCount: mockQuestions.length
      };

    case 'lonely':
      return {
        user: baseUser,
        stats: {
          connectionCount: 0,
          storyCount: 3,
          adultCount: 0,
          minorCount: 0
        },
        connections: [],
        questions: [],
        stories: mockStories,
        activities: [],
        notificationCount: 0
      };

    case 'active':
    default:
      return {
        user: baseUser,
        stats: {
          connectionCount: 6,
          storyCount: 3,
          adultCount: 3,
          minorCount: 3
        },
        connections: mockConnections,
        questions: mockQuestions,
        stories: mockStories,
        activities: mockActivities,
        notificationCount: 3
      };
  }
}
